import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("assistr_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("assistr_token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    localStorage.setItem("assistr_token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem("assistr_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Page - Mobile Design
const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [role, setRole] = useState("customer");
  const [error, setError] = useState("");
  const [providerBio, setProviderBio] = useState("");
  const [providerPhoto, setProviderPhoto] = useState("");
  const [serviceTypes, setServiceTypes] = useState([]);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const fetchTerms = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/legal/terms`);
      setTerms(response.data.terms);
    } catch (err) {
      console.error("Failed to fetch terms:", err);
    }
  }, []);

  const handleViewTerms = () => {
    if (!terms) {
      fetchTerms();
    }
    setShowTerms(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!isLogin && !termsAccepted) {
      setError("You must accept the terms of service to register");
      return;
    }
    
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        const userData = {
          email,
          password,
          full_name: fullName,
          phone,
          address: address || null,
          role,
          terms_accepted: termsAccepted,
        };
        if (role === "provider") {
          userData.provider_bio = providerBio;
          userData.provider_photo = providerPhoto;
          userData.service_types = serviceTypes;
        }
        await register(userData);
      }
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleServiceType = (type) => {
    setServiceTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const allServiceTypes = ["Handyman", "Plumbing", "Electrical", "HVAC", "Cleaning", "Moving", "Landscaping"];

  return (
    <div className="auth-screen">
      {/* Logo */}
      <div className="auth-logo">
        <img 
          src="https://customer-assets.emergentagent.com/job_proservice-14/artifacts/c1a3714c_assistr_logo.png" 
          alt="ASSISTR" 
          className="auth-logo-img"
        />
        <p>{isLogin ? "Sign in to your account" : "Create your account"}</p>
      </div>

      {/* Auth Card */}
      <div className="auth-card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              placeholder="your@email.com"
              required 
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              placeholder="••••••••"
              required 
            />
          </div>

          {!isLogin && (
            <>
              <div className="form-group">
                <label>Full Name</label>
                <input 
                  type="text" 
                  value={fullName} 
                  onChange={(e) => setFullName(e.target.value)} 
                  placeholder="John Doe"
                  required 
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input 
                  type="tel" 
                  value={phone} 
                  onChange={(e) => setPhone(e.target.value)} 
                  placeholder="+1 (555) 000-0000"
                  required 
                />
              </div>

              <div className="form-group">
                <label>I am a...</label>
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="customer">Customer</option>
                  <option value="provider">Service Provider</option>
                </select>
              </div>

              {role === "customer" && (
                <div className="form-group">
                  <label>Home Address</label>
                  <input 
                    type="text" 
                    value={address} 
                    onChange={(e) => setAddress(e.target.value)} 
                    placeholder="123 Main St, City, State"
                    required 
                  />
                </div>
              )}

              {role === "provider" && (
                <>
                  <div className="form-group">
                    <label>Professional Bio</label>
                    <textarea 
                      value={providerBio} 
                      onChange={(e) => setProviderBio(e.target.value)} 
                      placeholder="Tell customers about your experience..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Profile Photo URL (optional)</label>
                    <input 
                      type="url" 
                      value={providerPhoto} 
                      onChange={(e) => setProviderPhoto(e.target.value)} 
                      placeholder="https://..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Services You Offer</label>
                    <div className="service-pills">
                      {allServiceTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          className={`service-pill ${serviceTypes.includes(type) ? 'selected' : ''}`}
                          onClick={() => toggleServiceType(type)}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="form-group">
                <div className="checkbox-group">
                  <input 
                    type="checkbox" 
                    id="terms"
                    checked={termsAccepted} 
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <span>
                    I accept the{" "}
                    <button type="button" onClick={handleViewTerms}>
                      Terms of Service
                    </button>
                  </span>
                </div>
              </div>
            </>
          )}

          {error && <div className="auth-error">{error}</div>}

          <button 
            type="submit" 
            className="cta-button" 
            disabled={loading}
            data-testid="auth-submit-button"
          >
            {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
          </button>
        </form>

        <div className="auth-toggle">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button type="button" onClick={() => setIsLogin(!isLogin)}>
            {isLogin ? "Sign up" : "Sign in"}
          </button>
        </div>
      </div>

      {/* Terms Modal */}
      {showTerms && terms && (
        <div className="modal-overlay" onClick={() => setShowTerms(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Terms of Service</h3>
              <button className="modal-close" onClick={() => setShowTerms(false)}>×</button>
            </div>

            <div style={{ maxHeight: '50vh', overflowY: 'auto' }} className="hide-scrollbar">
              <p style={{ color: 'var(--muted)', fontSize: '12px', marginBottom: '16px' }}>
                Last Updated: {terms.last_updated}
              </p>

              {terms.sections.map((section, index) => (
                <div key={index} style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: 'var(--accent)', fontSize: '14px', marginBottom: '8px' }}>
                    {section.heading}
                  </h4>
                  <p style={{ fontSize: '13px', lineHeight: '1.5', color: 'var(--text)' }}>
                    {section.content}
                  </p>
                </div>
              ))}
            </div>

            <button
              className="cta-button"
              onClick={() => {
                setTermsAccepted(true);
                setShowTerms(false);
              }}
              style={{ marginTop: '16px' }}
            >
              I Accept
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/auth" />;
};

export { AuthProvider, useAuth, AuthPage, ProtectedRoute };
